import { Modal } from './modal';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isValidPersonName, trackCallbackConfirmation } from '@workspace/utils';
import { useMutationBesoinAide } from '../api/ws';
import { useGlobalForm } from '../contexts/global-form-context';
import { SmartForm, useSmartForm } from '../contexts/smart-form';
import { SelectOption } from '../types';
import { FieldCheckbox, FieldInput, FieldPatternInput, FieldRadioGroup, SmartButton } from './smart-form';
import { RadioCalendar } from './smart-form/radio-calendar';
import { BulleAide } from './bulle-aide';
import { HorairesRecontact } from './horaires-recontact';
import { RgpdInfoBloc } from './rgpd-info-bloc';
import { ConsentRecontactLegal } from './consent-recontact-legal';

const etreRapellerOptions = [
  { label: 'Au plus vite', value: true },
  { label: 'Une date précise', value: false },
] as unknown as SelectOption[];

const RAPPELER_CONSENT_LABEL_DEFAULT =
  "Afin d'être accompagné dans le cadre de ma démarche d'assurance, j'accepte qu’un expert moto me contacte par téléphone et/ou email.";

export function ModalRappeler() {
  const { others, initialState, config } = useGlobalForm();
  const consentLabel = config?.modalRappeler?.consentLabel ?? RAPPELER_CONSENT_LABEL_DEFAULT;
  const apporteur1 = others.apporteur1?.codeBelair;
  const apporteur = others?.apporteur?.codeBelair;

  const methods = useSmartForm();
  const mutation = useMutationBesoinAide();
  const [searchParams, setSearchParams] = useSearchParams();
  const [step, setStep] = useState<'contact' | 'calendar'>('contact');

  methods.watch('asap');

  const onSubmit = async (data: any) => {
    const finalData = {
        ap1: apporteur1.toString(),
        ...data,
        asap: data?.asap === true,
        token: others?.token,
        canal: initialState?.DemandeTarif?.Police?.Canal
    };

    if (!finalData.asap) {
      if (data?.date && data?.heure && step === 'calendar') {
        await mutation.mutateAsync(finalData);
        trackCallbackConfirmation(apporteur);
      } else if (step === 'calendar') {
        methods.setError('date', { type: 'required', message: 'Ce champs est requis' });
        return;
      } else if (step === 'contact') {
        setStep('calendar');
        return;
      }
    } else {
      methods.resetField('date');
      methods.resetField('heure');
      delete finalData.date;
      delete finalData.heure;

      await mutation.mutateAsync(finalData);
      trackCallbackConfirmation(apporteur);
    }

    setSearchParams();
  };

  useEffect(() => {
    if (searchParams.get('modal') === 'rappeler') {
      setStep('contact');
    }
  }, [searchParams]);

  return (
    <Modal
      className="max-w-3xl h-[calc(100vh-125px)] sm:max-h-[calc(100vh-125px)] sm:h-auto"
      onClose={() => {
        setSearchParams();
      }}
      open={searchParams.get('modal') === 'rappeler'}
      title="Vous souhaitez être rappelé ?"
      subTitle="Saisissez vos coordonnées"
      closeButton={true}
    >
      <Modal.Body className="pt-4 pb-8 mt-4 border-t overflow-y-scroll">
        <SmartForm
          {...methods}
          onSubmit={onSubmit}
        >
          <div className="space-y-4 w-full flex-1">
            {step === 'contact' && (
              <>
                <BulleAide title="Attention, si vous êtes déjà client, merci d'appeler au 02 47 51 07 07." />
                <FieldInput
                  name="prenom"
                  label="Prénom"
                  validation={{ validate: { isValidPersonName } }}
                />
                <FieldInput
                  name="nom"
                  label="Nom"
                  validation={{ validate: { isValidPersonName } }}
                />
                <FieldPatternInput
                  type="phone"
                  name="tel"
                  label="Téléphone"
                />
                <FieldRadioGroup
                  label="Quand voulez-vous être rappelé ?"
                  type="small"
                  name="asap"
                  col={1}
                  classNameGroup="sm:grid-cols-2 sm:gap-3"
                  options={etreRapellerOptions}
                />
                <HorairesRecontact />
                <FieldCheckbox
                  label={consentLabel}
                  name="accept"
                />
                <RgpdInfoBloc letterUrl={config?.rgpd?.letterUrl} controllerName={config?.rgpd?.controllerName} />
              </>
            )}

            {step === 'calendar' && <RadioCalendar />}
          </div>

          <div className="flex justify-center gap-4 w-full mt-4">
            {step === 'calendar' && (
              <SmartButton
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  methods.clearErrors();
                  setStep('contact');
                }}
                arrow={false}
              >
                Retour
              </SmartButton>
            )}
            <SmartButton
              type="submit"
              disabled={!methods.formState.isValid}
              size="sm"
              className="w-full sm:w-auto"
            >
              {step === 'contact' ? 'Continuer' : 'Valider'}
            </SmartButton>
          </div>

          {step === 'contact' && <ConsentRecontactLegal className="text-[11px] mt-4" />}
        </SmartForm>
      </Modal.Body>
    </Modal>
  );
}
